/* eslint-disable react/no-array-index-key */
import React from "react";
import {
  FarClone,
  FasTimesCircle,
  FasHistory,
  FasPlus,
  FasTrashAlt,
  FasFileSignature
} from "assets/icons";

import ProtectionElements from "protocol-setup/helpers/ProtectionElement";
import { RequestStatus, useSetupPermission } from "../../helpers"
import CreateRequestChange from "./CreateRequestChange";
import SubmitRequestBtn from "../FormComponents/SubmitRequestBtn";
import RequestHistoryButton from "../RequestHistory";
import DeleteRequest from "./DeleteRequest";
import CancelRequest from "./CancelRequest";
import ViewOtherFormButton from "./ViewOtherFormButton";

const getIcon = (iconType) => {
  let myIcon = null;
  switch (iconType) {
    case "plus":
      myIcon = <FasPlus className="ml-n1" />;
      break;
    case "history":
      myIcon = <FasHistory className="ml-n1" />;
      break;
    case "trash":
      myIcon = <FasTrashAlt className="ml-n1" />;
      break;
    case "cross":
      myIcon = <FasTimesCircle className="ml-n1" />;
      break;
    case "submit":
      myIcon = <FasFileSignature />;
      break;
    case "clone":
      myIcon = <FarClone className="mr-n1" />;
      break;
    default:
      break;
  }
  return myIcon;
};

const RequestActionBar = ({
  content,
  formPartType,
  isSubmitFormEnabled,
  setIsSubmitFormEnabled,
  history,
  fetchPsData,
  requestNumber,
  navigate,
  onSuccess,
  protocolNumber,
  requestStatus,
  lpo
}) => {

  const hasPermissionView = useSetupPermission(
    formPartType === 'OPEN_RAVE'
      ? ProtectionElements.OPEN_VIEW
      : ProtectionElements.REGULATORY_VIEW
  );

  const hasPermissionEdit = useSetupPermission(
    formPartType === 'OPEN_RAVE'
      ? ProtectionElements.OPEN_REQUEST
      : ProtectionElements.REGULATORY_REQUEST
  );

  // Any level of access: VIEW-only or full EDIT
  const hasAnyPermission = hasPermissionView || hasPermissionEdit;

  const hasAllowedStatus = (allowedStatus) =>
    allowedStatus ? allowedStatus.includes(requestStatus) : true;

  // View-level visibility: Status matches AND user has at least VIEW or EDIT permission
  const canUserViewButtons = (allowedStatus) =>
    hasAnyPermission && (allowedStatus.includes(requestStatus) || allowedStatus === "All");

  // Edit-level visibility: Status matches AND user has EDIT permission
  const canUserEditButtons = (allowedStatus) =>
    hasPermissionEdit && (allowedStatus ? (allowedStatus.includes(requestStatus) || allowedStatus === "All") : true);

  const canUserInteractWithButtons = (allowedStatus) =>
    hasPermissionEdit && hasAllowedStatus(allowedStatus);

  // Submit: only visible to users with edit permission
  const renderSubmitButton = (item) => {
    if (!hasPermissionEdit) return null;
    return (
      <SubmitRequestBtn
        requestNumber={item.requestNumber}
        formPartType={formPartType}
        isSubmitFormEnabled={isSubmitFormEnabled && canUserInteractWithButtons()}
        setIsSubmitFormEnabled={setIsSubmitFormEnabled}
        onSuccess={onSuccess}
        disabled={!canUserInteractWithButtons()}
      />
    );
  };

  // View History: visible to any user with at least VIEW or EDIT permission
  const renderHistoryButton = (item) => {
    if (!hasAnyPermission) return null;
    return (
      <RequestHistoryButton
        requestNumber={item.requestNumber}
        formPartType={formPartType}
        isSubmitFormEnabled={isSubmitFormEnabled}
        icon={getIcon(item.icon)}
        setIsSubmitFormEnabled={setIsSubmitFormEnabled}
      />
    );
  };

  // Delete: only visible to users with edit permission and correct status
  const renderDeleteButton = (item, index) => {
    if (!canUserEditButtons(item.status)) return null;
    return (
      <div
        className="summary-bar"
        key={`${item.requestNumber}_${index}`}
      >
        <DeleteRequest
          requestId={item.requestNumber}
          navigate={navigate}
          item={item}
          index={index}
          getIcon={getIcon}
          protocolNumber={protocolNumber}
          requestStatus={requestStatus}
          disabled={!canUserInteractWithButtons(item.status)}
        />
      </div>
    );
  };

  // Cancel: only visible to users with edit permission and correct status
  const renderCancelButton = (item, index) => {
    if (!canUserEditButtons(item.status)) return null;
    return (
      <div
        className="summary-bar"
        key={`${item.requestNumber}_${index}`}
      >
        <CancelRequest
          requestId={item.requestNumber}
          navigate={navigate}
          item={item}
          index={index}
          getIcon={getIcon}
          disabled={!canUserInteractWithButtons(item.status)}
        />
      </div>
    );
  };

  // View {form type}: visible to any user with at least VIEW or EDIT permission, when status matches
  const renderViewButton = (item) => {
    if (!canUserViewButtons(item.status)) return null;
    return (
      <ViewOtherFormButton
        formPartType={formPartType}
        protocolNumber={protocolNumber}
        lpo={lpo}
        item={item}
        disabled={!hasAllowedStatus(item.allowedStatus)}
      />
    );
  };

  // Add Request: only visible to users with edit permission and correct status
  const renderAddRequestButton = (item, index) => {
    if (!canUserEditButtons(item.status)) return null;
    return (
      <div className="summary-bar" key={`${item.requestNumber}_${index}`}>
        <CreateRequestChange
          requestNumber={requestNumber}
          formPartType={formPartType}
          fetchPsData={fetchPsData}
          history={history}
          protocolNumber={protocolNumber}
          lpo={lpo}
          requestStatus={requestStatus}
          item={item}
          getIcon={getIcon}
          disabled={!hasAllowedStatus(item.allowedStatus)}
        />
      </div>
    );
  };

  return (
    <>
      {content.map((item, index) => {
        if (!item.requestNumber || item.requestNumber == null) {
          return (
            <div className="summary-bar mr-2">
              <strong
                key={`${item.value}_${index}`}
                className="summary-bar-item"
              >
                {requestStatus && RequestStatus[requestStatus]}
              </strong>
            </div>
          );
        }
        
        if (item.key === "submit") return renderSubmitButton(item);
        if (item.key === "viewHistory") return renderHistoryButton(item);
        if (item.key === "delete") return renderDeleteButton(item, index);
        if (item.key === "cancel") return renderCancelButton(item, index);
        if (item.key === "viewRegulatory") return renderViewButton(item);
        if (item.key === "addRequest") return renderAddRequestButton(item, index);
        
        return null;
      })}
    </>
  );
};

export default RequestActionBar;
